import { environment } from 'src/environments';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseLoadTableParams } from './baseLoadTableParams';
import { Observable } from 'rxjs';
import { Patient, PatientFileInputModel, PatientFileModel, StatusesTranslatedItem } from '../models';

const basePath = `${environment.apiBaseUrl}patient`;

export interface GetPatientsApiPatientTreatment {
  id: string;
  treatmentTypeId: number;
  treatmentType: string;
  statusId: number;
  status: string;
  treatmentImage?: string;
  treatmentStatus?: StatusesTranslatedItem;
}

export interface GetPatientsApiPatient {
  id: number;
  fullName: string;
  patientId: string;
  initials: string;
  createdDate: string;
  modifiedDate: string;
  treatments: GetPatientsApiPatientTreatment[];
}

export interface GetPatientsApiResponse {
  data: GetPatientsApiPatient[];
  hasMore: boolean;
  startingAfter: string | null;
  skip: number;
}

export interface PatientUploadLinkModel {
  readonly url: string;
  readonly tags: string;
  readonly fileGuid: string;
  readonly filename: string;
}

@Injectable({ providedIn: 'root' })
export class PatientApiClient {
  private readonly _http = inject(HttpClient);

  save(model: Patient): Observable<Patient> {
    return this._http.post<Patient>(`${basePath}/save`, {
      ...model,
      dateOfBirth: model.dateOfBirth?.toDateString(),
    });
  }

  getPatients(filter: BaseLoadTableParams): Observable<GetPatientsApiResponse> {
    return this._http.post<GetPatientsApiResponse>(`${basePath}/v2/load-page`, filter);
  }

  getPatient(id: number) {
    return this._http.get<Patient>(`${basePath}/${id}`);
  }

  getUploadLink(id: number, filename: string): Observable<PatientUploadLinkModel> {
    return this._http.get<PatientUploadLinkModel>(
      `${basePath}/${id}/upload-link?filename=${encodeURIComponent(filename)}`
    );
  }

  getFiles(id: number): Observable<PatientFileModel[]> {
    return this._http.get<PatientFileModel[]>(`${basePath}/${id}/files`);
  }

  saveFileInfo(id: number, model: PatientFileInputModel): Observable<PatientFileModel> {
    return this._http.post<PatientFileModel>(`${basePath}/${id}/file`, model);
  }

  deleteFile(id: number, fileId: number): Observable<boolean> {
    return this._http.delete<boolean>(`${basePath}/${id}/file/${fileId}`);
  }

  getPatientByExternalId(id: string) {
    return this._http.get<Patient>(`${basePath}/by-external-id/${id}`);
  }

  markForDeletion(id: number): Observable<boolean> {
    return this._http.post<boolean>(`${basePath}/${id}/mark-for-deletion`, {});
  }
}
